import z from 'zod'
import { zColor, type Color } from './color'

export const zGradientStop = z.object({
  color: zColor,
  position: z.number().min(0).max(1),
  smoothness: z.number().min(0).max(100).optional(),
})

export const zLinearGradient = z.object({
  angle: z.number(),
  stops: z.array(zGradientStop).min(2),
})

export type GradientStop = z.infer<typeof zGradientStop>
export type LinearGradient = z.infer<typeof zLinearGradient>

/** Per-corner border-radius override, in CSS shorthand order [tl, tr, br, bl].
 *  When present, drawCanvas uses these instead of the uniform `borderRadius`.
 *  Cleared by the editor any time the uniform-radius slider is touched. */
export const zBorderRadiusCorners = z.tuple([z.number(), z.number(), z.number(), z.number()])
export type BorderRadiusCorners = z.infer<typeof zBorderRadiusCorners>

export const zTextOptions = z.object({
  fontFamily: z.enum(['serif', 'sans-serif']).or(z.string()),
  fontSize: z.number(),
  fontWeight: z.number(),
  fontStyle: z.enum(['normal', 'italic']),
  fontColor: zColor,
  lineHeight: z.number(),
  strokeWidth: z.number(),
  strokeColor: zColor,
  horizontalAlign: z.enum(['left', 'center', 'right']),
  verticalAlign: z.enum(['top', 'center', 'bottom']),
  /** Inner padding inside the node bounds. Reserved for the text content
   *  area so left/right-aligned text starts inside the box, and the
   *  backdrop fills the full padded box. Optional — legacy nodes and
   *  templates that omit these continue to render unchanged (treated as 0). */
  paddingTop: z.number().optional(),
  paddingRight: z.number().optional(),
  paddingBottom: z.number().optional(),
  paddingLeft: z.number().optional(),
  /** Effective opacity applied to the whole text element (product of CSS
   *  opacity along the source ancestor chain). Optional — legacy nodes
   *  treat absence as 1. */
  opacity: z.number().optional(),
  /** Per-line layout captured from the browser at extraction time. When
   *  present, drawCanvas paints each line at its captured position instead
   *  of re-deriving wrap and font metrics — gives pixel-accurate fidelity
   *  for the initial render. Cleared the moment the user edits text content.
   *
   *  `runs` holds the inline segments within a line (e.g. a `<strong>` or a
   *  `<span style="color:...">` inside the data-role'd element). For new
   *  extractions, runs carry only the positional cache (`text`, `x`) — the
   *  styling lives on `data.content` runs and is matched at paint time by
   *  character offset. The style fields below are deprecated and persist
   *  only for legacy nodes extracted before the styling consolidation. */
  preWrappedLines: z
    .array(
      z.object({
        text: z.string(),
        x: z.number(),
        y: z.number(),
        width: z.number(),
        height: z.number(),
        runs: z
          .array(
            z.object({
              text: z.string(),
              /** X position relative to the line's `x`. */
              x: z.number(),
              fontFamily: z.string().optional(),
              fontSize: z.number().optional(),
              fontWeight: z.number().optional(),
              fontStyle: z.enum(['normal', 'italic']).optional(),
              color: zColor.optional(),
            }),
          )
          .optional(),
      }),
    )
    .optional(),
  backdropColor: zColor,
  backdropPadding: z.number(),
  backdropBorderRadius: z.number(),
  backdropBorderRadiusCorners: zBorderRadiusCorners.optional(),
  backdropBorderWidth: z.number(),
  backdropBorderColor: zColor,
  backdropGradient: zLinearGradient.optional(),
})

export type TextOptions = z.infer<typeof zTextOptions>

/** A single styled segment of a text node's content. The full content is an
 *  ordered array of these runs; `\n` inside `text` is a hard line break.
 *
 *  Absolute-styling model: every run carries every text-styling field
 *  explicitly. Changing a node-level "default" (via toolbar, "select all
 *  → set color", etc.) walks the runs and rewrites them — no inheritance
 *  resolution at render time. This matches Lexical's node model and keeps
 *  the canvas paint path simple. */
export const zRichTextRun = z.object({
  text: z.string(),
  fontFamily: z.string(),
  fontSize: z.number(),
  fontWeight: z.number(),
  fontStyle: z.enum(['normal', 'italic']),
  fontColor: zColor,
})

export type RichTextRun = z.infer<typeof zRichTextRun>

/** Wrap a flat string into a single-run array, taking style from the node's
 *  TextOptions defaults. Use this anywhere a producer used to write a string. */
const stringToRunsForCoercion = (text: string, options: TextOptions): RichTextRun[] => [
  {
    text,
    fontFamily: options.fontFamily,
    fontSize: options.fontSize,
    fontWeight: options.fontWeight,
    fontStyle: options.fontStyle,
    fontColor: options.fontColor,
  },
]

/** Coerce a legacy string-shape content value into a single-run array.
 *  Used as a defensive normalization at parse boundaries (zod) and inside
 *  the runtime helpers, so even unmigrated rows or stale-tab writes that
 *  send `content: "string"` don't crash the renderer. */
const coerceContentToRuns = (content: unknown, options: TextOptions | undefined): RichTextRun[] => {
  if (Array.isArray(content)) return content as RichTextRun[]
  if (typeof content === 'string' && options) return stringToRunsForCoercion(content, options)
  return []
}

export const zTextNodeData = z
  .object({
    type: z.literal('text'),
    isLoading: z.boolean(),
    content: z.unknown(),
    editMode: z.boolean(),
    options: zTextOptions,
  })
  .transform((d) => ({
    ...d,
    content: coerceContentToRuns(d.content, d.options),
  }))
  .pipe(
    z.object({
      type: z.literal('text'),
      isLoading: z.boolean(),
      /** Ordered array of styled runs. Legacy rows that still hold a flat
       *  string get coerced to a single-run array via `coerceContentToRuns`
       *  in the preceding transform. */
      content: z.array(zRichTextRun),
      editMode: z.boolean(),
      options: zTextOptions,
    }),
  )

export type TextNodeData = z.infer<typeof zTextNodeData>

/** Flatten a runs array to plain text. Equivalent to the legacy `string`
 *  content for any caller that doesn't care about per-run styling.
 *  Defensive against non-array inputs so an unmigrated string content
 *  doesn't crash the runtime path. */
export const runsToString = (runs: RichTextRun[]): string => {
  if (!Array.isArray(runs)) return typeof runs === 'string' ? runs : ''
  return runs.map((r) => r.text).join('')
}

/** Return the run that covers the given character offset into `runsToString(runs)`,
 *  or the last run if the offset is past the end. Used by the canvas renderer
 *  to look up styling for a `preWrappedLines` run-fragment by its position
 *  in the joined content. */
export const runAtCharOffset = (runs: RichTextRun[], offset: number): RichTextRun | undefined => {
  if (!Array.isArray(runs)) return undefined
  let consumed = 0
  for (const run of runs) {
    if (offset < consumed + run.text.length) return run
    consumed += run.text.length
  }
  return runs[runs.length - 1]
}

/** Wrap a flat string into a single-run array, taking style from the node's
 *  TextOptions defaults. Use this anywhere a producer used to write a string. */
export const stringToRuns = (text: string, options: TextOptions): RichTextRun[] => [
  {
    text,
    fontFamily: options.fontFamily,
    fontSize: options.fontSize,
    fontWeight: options.fontWeight,
    fontStyle: options.fontStyle,
    fontColor: options.fontColor,
  },
]

/** The five glyph-styling fields a single run carries. The toolbar reads these
 *  for display and writes them on change; the canvas paints with them. */
export type EffectiveTextStyle = {
  fontFamily: string
  fontSize: number
  fontWeight: number
  fontStyle: 'normal' | 'italic'
  fontColor: Color
}

/** Derive the glyph style the canvas will paint, from a text node's data.
 *
 *  Precedence mirrors `drawCanvas`:
 *   1. First inline run captured in `preWrappedLines` (autodesign fast path —
 *      runs override the node-level options at paint time).
 *   2. First `content` run (set by `stringToRuns` after the user edits).
 *   3. Node-level `TextOptions` (legacy fallback for nodes pre-runs migration).
 *
 *  The toolbar and editing textarea must use this — reading `options.fontColor`
 *  directly drifts away from what the canvas actually paints whenever runs
 *  carry their own styling. */
export const getEffectiveTextStyle = (data: TextNodeData): EffectiveTextStyle => {
  const opts = data.options
  // Legacy preWrappedLines runs may carry styling directly (pre-consolidation
  // nodes). New extractions leave these fields undefined and the styling
  // resolves from `content` runs below.
  if (opts.preWrappedLines) {
    for (const line of opts.preWrappedLines) {
      const first = line.runs?.[0]
      if (first && first.fontFamily !== undefined) {
        return {
          fontFamily: first.fontFamily,
          fontSize: first.fontSize ?? opts.fontSize,
          fontWeight: first.fontWeight ?? opts.fontWeight,
          fontStyle: first.fontStyle ?? opts.fontStyle,
          fontColor: first.color ?? opts.fontColor,
        }
      }
    }
  }
  const firstContentRun = data.content[0]
  if (firstContentRun) {
    return {
      fontFamily: firstContentRun.fontFamily,
      fontSize: firstContentRun.fontSize,
      fontWeight: firstContentRun.fontWeight,
      fontStyle: firstContentRun.fontStyle,
      fontColor: firstContentRun.fontColor,
    }
  }
  return {
    fontFamily: opts.fontFamily,
    fontSize: opts.fontSize,
    fontWeight: opts.fontWeight,
    fontStyle: opts.fontStyle,
    fontColor: opts.fontColor,
  }
}

const EFFECTIVE_STYLE_KEYS: readonly (keyof EffectiveTextStyle)[] = [
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'fontColor',
]

/** Merge the displayed effective style into a TextOptions partial. The
 *  store calls this before applying a toolbar update that will invalidate
 *  `preWrappedLines`: once invalidated, the canvas paints from TextOptions
 *  alone, and any style fields the user *didn't* touch must already hold
 *  the values they were seeing on canvas — not the host element's defaults
 *  captured at extraction time. */
export const bakeEffectiveStyleIntoOptionsUpdate = (
  data: TextNodeData,
  optionsUpdate: Partial<TextOptions>,
): Partial<TextOptions> => {
  const effective = getEffectiveTextStyle(data)
  const baked: Partial<TextOptions> = { ...optionsUpdate }
  for (const key of EFFECTIVE_STYLE_KEYS) {
    if (baked[key] === undefined) {
      // @ts-expect-error narrowing across the union of style fields
      baked[key] = effective[key]
    }
  }
  return baked
}

export const zImageOptions = z.object({
  mode: z.enum(['contain', 'cover', 'distort']),
  opacity: z.number(),
  borderRadius: z.number(),
  borderRadiusCorners: zBorderRadiusCorners.optional(),
  borderWidth: z.number(),
  borderColor: zColor,
})

export type ImageOptions = z.infer<typeof zImageOptions>

export const zImageNodeData = z.object({
  type: z.literal('image'),
  isLoading: z.boolean(),
  source: z.object({ type: z.literal('url'), url: z.string() }),
  options: zImageOptions,
})

export type ImageNodeData = z.infer<typeof zImageNodeData>

export const zShapeOptions = z.object({
  shape: z.enum(['rectangle', 'circle', 'triangle']),
  fillColor: zColor,
  fillOpacity: z.number(),
  strokeColor: zColor,
  strokeOpacity: z.number(),
  lineWidth: z.number(),
  borderRadius: z.number(),
  borderRadiusCorners: zBorderRadiusCorners.optional(),
  fillGradient: zLinearGradient.optional(),
})

export type ShapeOptions = z.infer<typeof zShapeOptions>

export const zShapeNodeData = z.object({
  type: z.literal('shape'),
  isLoading: z.boolean(),
  options: zShapeOptions,
})

export type ShapeNodeData = z.infer<typeof zShapeNodeData>

export const zLayoutNode = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  rotation: z.number(),
  visible: z.boolean(),
  //   blendingMode: z.string(),
  //   filter: z.string(),
  data: zTextNodeData.or(zImageNodeData).or(zShapeNodeData),
  metadata: z.object({ role: z.string() }).optional(),
  groupId: z.string().optional(),
  // Locked layers stay individually selectable and editable, but are excluded from
  // marquee selection and can't be dragged, resized, or rotated. Optional so existing
  // persisted nodes (a loose JSON blob) default to unlocked.
  locked: z.boolean().optional(),
})

export type LayoutNode = z.infer<typeof zLayoutNode>
